/**
 * AddToCalendar - Button/dropdown to add events/sessions to calendar
 * Supports: Google Calendar, iCal download, Outlook
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar, Download, ExternalLink, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { calendarAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddToCalendar = ({ 
  eventId, 
  sessionId = null, 
  title = "Add to Calendar",
  variant = "outline",
  size = "sm",
  className = ""
}) => {
  const [loading, setLoading] = useState(false);

  // Get iCal download URL
  const getIcalUrl = () => {
    if (sessionId) {
      return `${API_URL}/api/events/${eventId}/sessions/${sessionId}/calendar.ics`;
    }
    return `${API_URL}/api/events/${eventId}/calendar.ics`;
  };

  // Handle Google Calendar click
  const handleGoogleCalendar = async () => {
    setLoading(true);
    try {
      const response = sessionId 
        ? await calendarAPI.getSessionGoogleLink(eventId, sessionId)
        : await calendarAPI.getEventGoogleLink(eventId);
      
      if (response.data?.google_calendar_url) {
        window.open(response.data.google_calendar_url, '_blank');
      } else {
        toast.error('Could not generate calendar link');
      }
    } catch (error) {
      toast.error('Failed to generate Google Calendar link');
    } finally {
      setLoading(false);
    }
  };

  // Handle iCal download
  const handleIcalDownload = () => {
    const url = getIcalUrl();
    window.open(url, '_blank');
    toast.success('Calendar file downloading...');
  };

  // Handle Outlook web
  const handleOutlook = async () => {
    // Outlook.com uses a similar URL structure to Google
    setLoading(true);
    try {
      const response = sessionId 
        ? await calendarAPI.getSessionGoogleLink(eventId, sessionId)
        : await calendarAPI.getEventGoogleLink(eventId);
      
      if (response.data) {
        const { start, end } = response.data;
        const eventTitle = response.data.session_title || response.data.event_name || 'Event';
        
        // Outlook.com calendar URL
        const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(eventTitle)}&startdt=${start}&enddt=${end}`;
        window.open(outlookUrl, '_blank');
      }
    } catch (error) {
      toast.error('Failed to generate Outlook link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={className}
          disabled={loading}
          data-testid="add-to-calendar-btn"
        >
          <Calendar className="h-4 w-4 mr-2" />
          {title}
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleGoogleCalendar} data-testid="google-calendar-option">
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google Calendar
          <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleOutlook} data-testid="outlook-calendar-option">
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.353.233-.578.233h-8.168v-6.29l1.244.927c.158.112.342.168.54.168.197 0 .38-.056.54-.168l6.66-4.966v-.958z"/>
            <path fill="#0078D4" d="M23.762 5.443L17.16 10.37l-2.144-1.6V5.674c0-.23.08-.424.238-.576.158-.152.353-.233.578-.233h7.93z"/>
            <path fill="#0078D4" d="M14.318 0c.316 0 .592.1.824.3.232.2.356.452.37.758v3.058l-5.344 4.312L0 1.058V.758C.014.452.138.2.37.3.602.1.878 0 1.194 0h13.124z"/>
            <path fill="#28A8EA" d="M0 5.674v11.998c0 .306.124.558.37.758.232.2.508.3.824.3h8.474V8.428L0 5.674z"/>
          </svg>
          Outlook
          <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleIcalDownload} data-testid="ical-download-option">
          <Download className="h-4 w-4 mr-2" />
          Download .ics
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCalendar;
